import { Component, OnDestroy, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subscription } from 'rxjs';
import { distinctUntilChanged, skip } from 'rxjs/operators';
import {
  DatePickerComponent,
  FloatingActionFabComponent,
  PageActionModalComponent,
  PageContextGuardComponent,
  PageShellComponent,
  SlotCalendarItem,
  SlotCalendarComponent,
} from '@clinic-platform/ui-components';

import { TranslatePipe } from '../../../core/i18n/translate.pipe';
import { PageHeaderComponent } from '../../../shared/ui/page-header/page-header.component';
import { LoadingBlockComponent } from '../../../shared/ui/loading-block/loading-block.component';
import { EmptyStateComponent } from '../../../shared/ui/empty-state/empty-state.component';
import { CompanyContextRequiredComponent } from '../../../shared/ui/company-context-required/company-context-required.component';
import { resolveHttpErrorMessageKey } from '../../../core/http/http-error-message.util';
import { SlotItem, SlotListFilters, SlotListPage, SlotListSort } from '../data-access/slots.models';
import { SlotsService } from '../data-access/slots.service';
import { SlotsTableComponent } from '../components/slots-table.component';
import { AuthService } from '../../../core/auth/auth.service';
import { DevicesService } from '../../devices/data-access/devices.service';
import { DeviceItem } from '../../devices/data-access/devices.models';
import { DeviceQuickViewModalComponent } from '../../devices/components/device-quick-view-modal.component';
import { ClinicQuickViewModalComponent } from '../../clinics/components/clinic-quick-view-modal.component';
import { SpecialistQuickViewModalComponent } from '../../specialists/components/specialist-quick-view-modal.component';
import { UsersService } from '../../users/data-access/users.service';
import { UserItem } from '../../users/data-access/users.models';
import { SettingsService } from '../../settings/data-access/settings.service';
import { ClinicOperationalSettingsDto } from '../../settings/data-access/settings.models';
import { SystemAdminContextService } from '../../../core/context/system-admin-context.service';
import { AppointmentCreateFlowComponent } from '../../appointments/components/appointment-create-flow.component';
import { AppointmentFormPrefill } from '../../appointments/components/appointment-form.component';

type SlotsViewMode = 'table' | 'calendar' | 'capacity';

interface SlotsStaffSummaryItem {
  label: string;
  value: string;
  tone: 'neutral' | 'good' | 'warning' | 'danger';
}

@Component({
  selector: 'app-slots-page',
  standalone: true,
  imports: [
    PageHeaderComponent,
    LoadingBlockComponent,
    EmptyStateComponent,
    CompanyContextRequiredComponent,
    ReactiveFormsModule,
    PageContextGuardComponent,
    SlotsTableComponent,
    SlotCalendarComponent,
    FloatingActionFabComponent,
    DatePickerComponent,
    PageActionModalComponent,
    PageShellComponent,
    AppointmentCreateFlowComponent,
    DeviceQuickViewModalComponent,
    ClinicQuickViewModalComponent,
    SpecialistQuickViewModalComponent,
    TranslatePipe
  ],
  template: `
    <lib-page-shell>
      <app-page-header
        title="Slots"
        subtitle="Operational view of slot inventory"
      />

      <div class="slots-page__toolbar">
        <div class="slots-page__day-nav">
          <lib-date-picker
            [label]="'Date' | t"
            [value]="selectedDayKey"
            inputId="slots-selected-day"
            (valueChange)="onSelectedDayChanged($event)"
          />
        </div>

        <div class="slots-page__view-switch">
          <button
            type="button"
            [class.slots-page__view-button--active]="viewMode === 'table'"
            class="slots-page__view-button"
            (click)="setViewMode('table')"
          >
            {{ 'Table' | t }}
          </button>

          <button
            type="button"
            [class.slots-page__view-button--active]="viewMode === 'calendar'"
            class="slots-page__view-button"
            (click)="setViewMode('calendar')"
          >
            {{ 'Calendar' | t }}
          </button>

          <button
            type="button"
            [class.slots-page__view-button--active]="viewMode === 'capacity'"
            class="slots-page__view-button"
            (click)="setViewMode('capacity')"
          >
            Capacity
          </button>
        </div>
      </div>

      <lib-page-context-guard [blocked]="companyContextMissing">
        <app-company-context-required />
      </lib-page-context-guard>

      @if (!companyContextMissing && isLoading) {
        <app-loading-block text="Loading slots..." />
      } @else if (!companyContextMissing && errorText) {
        <app-empty-state
          [title]="errorText === 'error.settingsCompanyContextRequired' ? 'Company is required' : 'Failed to load slots'"
          [description]="errorText"
        />
      } @else if (!companyContextMissing && items.length === 0) {
        <app-empty-state
          title="No slots found"
          description="No slots matched the current filters."
        />
      } @else if (!companyContextMissing) {
        @if (viewMode === 'table') {
          <app-slots-table
            [items]="items"
            [clinics]="clinicOptions"
            [devices]="deviceOptions"
            [specialists]="specialistOptions"
            [clinicTimeZones]="clinicTimeZones"
            [canCreateAppointment]="canCreateAppointment"
            [canOpenDevice]="canOpenDevice"
            [canOpenClinic]="canOpenClinic"
            [canOpenSpecialist]="canOpenSpecialist"
            (createAppointmentClicked)="onCreateAppointment($event)"
            (deviceOpenClicked)="openDeviceQuickView($event)"
            (clinicOpenClicked)="openClinicQuickView($event)"
            (specialistOpenClicked)="openSpecialistQuickView($event)"
          />
          @if (hasMoreSlots || isLoadingMore) {
            <div class="slots-page__load-more">
              <button
                type="button"
                class="slots-page__load-more-button"
                [disabled]="isLoadingMore"
                (click)="loadMoreSlots()"
              >
                {{ isLoadingMore ? ('Loading more...' | t) : ('Load more slots' | t) }}
              </button>
            </div>
          }
        } @else if (viewMode === 'calendar') {
          <section class="slots-page__staff-calendar">
            <div class="slots-page__staff-summary" aria-label="Staff calendar summary">
              @for (item of staffCalendarSummaryItems; track item.label) {
                <div
                  class="slots-page__summary-tile"
                  [class.slots-page__summary-tile--good]="item.tone === 'good'"
                  [class.slots-page__summary-tile--warning]="item.tone === 'warning'"
                  [class.slots-page__summary-tile--danger]="item.tone === 'danger'"
                >
                  <span class="slots-page__summary-label">{{ item.label | t }}</span>
                  <strong class="slots-page__summary-value">{{ item.value }}</strong>
                </div>
              }
            </div>

            <lib-slot-calendar
              [slots]="calendarItems"
              [selectedDate]="selectedDayKey"
              [rangeStartDate]="calendarRangeStartDate"
              [rangeEndDate]="calendarRangeEndDate"
              [showDatePicker]="false"
              [emptySlotsText]="'No slot data available for calendar view.' | t"
              [emptyDateText]="'No slots available for selected date.' | t"
              [selectableStatuses]="calendarSelectableStatuses"
              [allowPastSelection]="false"
              [disabled]="!canCreateAppointment"
              [showPastStatus]="true"
              [availableLabel]="'Available' | t"
              [reservedLabel]="'Reserved' | t"
              [blockedLabel]="'Blocked' | t"
              [completedLabel]="'Completed' | t"
              [pastLabel]="'Past' | t"
              (slotSelected)="onCalendarSlotSelected($event)"
            />
          </section>
        } @else {
          <section class="slots-page__staff-calendar">
            <div class="slots-page__staff-summary" aria-label="Staff capacity summary">
              @for (item of staffCapacitySummaryItems; track item.label) {
                <div
                  class="slots-page__summary-tile"
                  [class.slots-page__summary-tile--good]="item.tone === 'good'"
                  [class.slots-page__summary-tile--warning]="item.tone === 'warning'"
                  [class.slots-page__summary-tile--danger]="item.tone === 'danger'"
                >
                  <span class="slots-page__summary-label">{{ item.label | t }}</span>
                  <strong class="slots-page__summary-value">{{ item.value }}</strong>
                </div>
              }
            </div>

            <lib-slot-calendar
              [slots]="capacityCalendarItems"
              [selectedDate]="selectedDayKey"
              [rangeStartDate]="calendarRangeStartDate"
              [rangeEndDate]="calendarRangeEndDate"
              [timeZone]="selectedSchedulingTimeZone"
              [showDatePicker]="false"
              [emptySlotsText]="'No capacity data available.' | t"
              [emptyDateText]="'No capacity data for selected date.' | t"
              [selectableStatuses]="calendarSelectableStatuses"
              [allowPastSelection]="false"
              [disabled]="!canCreateAppointment"
              [showPastStatus]="true"
              [legendMode]="'capacity'"
              (slotSelected)="onCalendarSlotSelected($event)"
            />
          </section>
        }
      }

      @if (!companyContextMissing) {
        <lib-floating-action-fab
          [menuTemplate]="calendarQuickActionsMenu"
          buttonAriaLabel="Open slots quick actions"
        />

        <ng-template #calendarQuickActionsMenu let-close="close">
          <div class="slots-page__fab-menu-grid">
            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Open slot filters"
              title="Open slot filters"
              (click)="openFilterModal(); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--filter" aria-hidden="true"></span>
            </button>

            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Previous day"
              title="Previous day"
              (click)="shiftSelectedDay(-1); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--prev" aria-hidden="true"></span>
            </button>

            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Today"
              title="Today"
              (click)="goToToday(); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--today" aria-hidden="true"></span>
            </button>

            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Next day"
              title="Next day"
              (click)="shiftSelectedDay(1); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--next" aria-hidden="true"></span>
            </button>

            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Back 7 days"
              title="Back 7 days"
              (click)="shiftSelectedDay(-7); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--prev-week" aria-hidden="true"></span>
            </button>

            <button
              type="button"
              class="slots-page__fab-action"
              aria-label="Forward 7 days"
              title="Forward 7 days"
              (click)="shiftSelectedDay(7); close()"
            >
              <span class="slots-page__fab-icon slots-page__fab-icon--next-week" aria-hidden="true"></span>
            </button>
          </div>
        </ng-template>
      }

      @if (isFilterModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Filters & Sort"
          subtitle="Device, specialist, status, and sort controls for slots"
          width="760px"
          (close)="closeFilterModal()"
        >
            <form
              class="slots-page__modal-filters"
              [formGroup]="filtersForm"
              (submit)="$event.preventDefault(); onApplyFilters(); closeFilterModal()"
            >
              <div class="slots-page__modal-grid">
                <div class="slots-page__modal-field">
                  <label for="slots-modal-deviceId">{{ 'Device' | t }}</label>
                  <select
                    id="slots-modal-deviceId"
                    formControlName="deviceId"
                  >
                    <option value="">{{ 'slots.allDevices' | t }}</option>
                    @for (item of visibleDeviceOptions; track item.id) {
                      <option [value]="item.id">{{ item.label }}</option>
                    }
                  </select>
                </div>

                <div class="slots-page__modal-field">
                  <label for="slots-modal-specialistUserId">{{ 'Specialist' | t }}</label>
                  <select
                    id="slots-modal-specialistUserId"
                    formControlName="specialistUserId"
                  >
                    <option value="">{{ 'slots.allSpecialists' | t }}</option>
                    @for (item of visibleSpecialistOptions; track item.id) {
                      <option [value]="item.id">{{ item.label }}</option>
                    }
                  </select>
                </div>

                <div class="slots-page__modal-field">
                  <label for="slots-modal-status">{{ 'common.status' | t }}</label>
                  <select
                    id="slots-modal-status"
                    formControlName="status"
                  >
                    <option value="all">{{ 'All' | t }}</option>
                    @for (status of slotStatuses; track status) {
                      <option [value]="status">{{ ('status.' + status) | t }}</option>
                    }
                  </select>
                </div>

                <div class="slots-page__modal-field">
                  <label for="slots-modal-sort">{{ 'Sort' | t }}</label>
                  <select
                    id="slots-modal-sort"
                    formControlName="sort"
                  >
                    @for (sort of slotSorts; track sort) {
                      <option [value]="sort">{{ ('slots.sort.' + sort) | t }}</option>
                    }
                  </select>
                </div>
              </div>

              <div class="slots-page__modal-actions">
                <button type="button" class="slots-page__modal-secondary" (click)="onResetFilters(); closeFilterModal()">
                  {{ 'Reset' | t }}
                </button>
                <button type="submit" class="slots-page__modal-primary">
                  {{ 'Apply Filters' | t }}
                </button>
              </div>
            </form>
        </lib-page-action-modal>
      }

      @if (isCreateAppointmentModalOpen) {
        <lib-page-action-modal
          [isOpen]="true"
          title="Create Appointment"
          subtitle="Slot context is prefilled from the selected availability."
          width="1040px"
          (close)="closeCreateAppointmentModal()"
        >
            <app-appointment-create-flow
              [prefill]="appointmentCreatePrefill"
              [navigateOnSuccess]="false"
              [availabilityCalendarMode]="false"
              [selectPrefilledSlotOnOpen]="true"
              (created)="handleAppointmentCreatedFromModal()"
            />
        </lib-page-action-modal>
      }

      @if (isDeviceQuickViewOpen && selectedDeviceId) {
        <app-device-quick-view-modal
          [isOpen]="true"
          [deviceId]="selectedDeviceId"
          title="Device Quick View"
          (close)="closeDeviceQuickView()"
        />
      }

      @if (isClinicQuickViewOpen && selectedClinicId) {
        <app-clinic-quick-view-modal
          [isOpen]="true"
          [clinicId]="selectedClinicId"
          title="Clinic Quick View"
          (close)="closeClinicQuickView()"
        />
      }

      @if (isSpecialistQuickViewOpen && selectedSpecialistUserId) {
        <app-specialist-quick-view-modal
          [isOpen]="true"
          [specialistUserId]="selectedSpecialistUserId"
          title="Specialist Quick View"
          (close)="closeSpecialistQuickView()"
        />
      }
    </lib-page-shell>
  `,
  styles: [`
    .slots-page__toolbar {
      margin-bottom: 20px;
      display: flex;
      justify-content: space-between;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .slots-page__day-nav {
      display: inline-flex;
      align-items: center;
      gap: 12px;
      flex-wrap: wrap;
    }

    .slots-page__view-switch {
      display: inline-flex;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      overflow: hidden;
      background: #ffffff;
    }

    .slots-page__view-button {
      height: 36px;
      min-width: 100px;
      border: none;
      background: #ffffff;
      color: #374151;
      cursor: pointer;
      padding: 0 14px;
      font-size: 13px;
      font-weight: 500;
    }

    .slots-page__view-button--active {
      background: #2563eb;
      color: #ffffff;
    }

    .slots-page__staff-calendar {
      display: grid;
      gap: 14px;
    }

    .slots-page__staff-summary {
      display: grid;
      grid-template-columns: repeat(auto-fit, minmax(138px, 1fr));
      gap: 10px;
    }

    .slots-page__summary-tile {
      min-height: 72px;
      border: 1px solid #d8dee8;
      border-left: 4px solid #94a3b8;
      border-radius: 8px;
      background: #ffffff;
      padding: 11px 12px;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      gap: 6px;
    }

    .slots-page__summary-tile--good {
      border-left-color: #16a34a;
    }

    .slots-page__summary-tile--warning {
      border-left-color: #d97706;
    }

    .slots-page__summary-tile--danger {
      border-left-color: #dc2626;
    }

    .slots-page__summary-label {
      color: #64748b;
      font-size: 12px;
      font-weight: 700;
      line-height: 1.25;
    }

    .slots-page__summary-value {
      color: #111827;
      font-size: 20px;
      line-height: 1.1;
      overflow-wrap: anywhere;
    }

    .slots-page__fab-menu-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 8px;
      width: 176px;
    }

    .slots-page__fab-menu-grid button.slots-page__fab-action {
      width: 48px;
      min-width: 48px;
      min-height: 48px;
      justify-content: center;
      padding: 0;
      border-radius: 14px;
    }

    .slots-page__fab-icon {
      position: relative;
      display: inline-flex;
      width: 18px;
      height: 18px;
      color: #0f172a;
      align-items: center;
      justify-content: center;
      flex-shrink: 0;
    }

    .slots-page__fab-icon--prev::before,
    .slots-page__fab-icon--next::before,
    .slots-page__fab-icon--prev-week::before,
    .slots-page__fab-icon--next-week::before {
      content: '';
      width: 8px;
      height: 8px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
      display: block;
    }

    .slots-page__fab-icon--prev::before {
      transform: rotate(-135deg);
      margin-left: 4px;
    }

    .slots-page__fab-icon--next::before {
      transform: rotate(45deg);
      margin-right: 4px;
    }

    .slots-page__fab-icon--prev-week::before {
      transform: rotate(-135deg);
      margin-left: 7px;
      box-shadow: -5px 0 0 -1px transparent;
    }

    .slots-page__fab-icon--prev-week::after {
      content: '';
      position: absolute;
      left: 2px;
      width: 8px;
      height: 8px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
      transform: rotate(-135deg);
    }

    .slots-page__fab-icon--next-week::before {
      transform: rotate(45deg);
      margin-right: 7px;
    }

    .slots-page__fab-icon--next-week::after {
      content: '';
      position: absolute;
      right: 2px;
      width: 8px;
      height: 8px;
      border-top: 2px solid currentColor;
      border-right: 2px solid currentColor;
      transform: rotate(45deg);
    }

    .slots-page__fab-icon--today::before {
      content: '';
      width: 10px;
      height: 10px;
      border-radius: 999px;
      border: 2px solid currentColor;
      display: block;
    }

    .slots-page__fab-icon--filter::before {
      content: '';
      width: 14px;
      height: 0;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 8px solid currentColor;
      position: absolute;
      top: 1px;
    }

    .slots-page__fab-icon--filter::after {
      content: '';
      position: absolute;
      top: 10px;
      width: 4px;
      height: 6px;
      background: currentColor;
      border-radius: 0 0 2px 2px;
    }

    .slots-page__modal-filters {
      display: grid;
      gap: 16px;
    }

    .slots-page__modal-grid {
      display: grid;
      grid-template-columns: repeat(3, minmax(0, 1fr));
      gap: 16px;
    }

    .slots-page__modal-field {
      display: flex;
      flex-direction: column;
      gap: 8px;
    }

    .slots-page__modal-field label {
      font-size: 13px;
      font-weight: 600;
      color: #374151;
    }

    .slots-page__modal-field select {
      height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      padding: 0 12px;
      font-size: 14px;
      box-sizing: border-box;
      background: #ffffff;
    }

    .slots-page__modal-actions {
      display: flex;
      justify-content: flex-end;
      gap: 10px;
    }

    .slots-page__load-more {
      display: flex;
      justify-content: center;
      padding: 18px 0 4px;
    }

    .slots-page__load-more-button {
      min-height: 40px;
      border: 1px solid #d1d5db;
      border-radius: 10px;
      background: #ffffff;
      color: #334155;
      padding: 0 16px;
      font-weight: 600;
      cursor: pointer;
    }

    .slots-page__load-more-button:disabled {
      cursor: not-allowed;
      opacity: 0.65;
    }

    .slots-page__modal-secondary,
    .slots-page__modal-primary {
      min-height: 40px;
      border-radius: 10px;
      padding: 0 14px;
      font-weight: 600;
      cursor: pointer;
    }

    .slots-page__modal-secondary {
      border: 1px solid #d1d5db;
      background: #ffffff;
      color: #334155;
    }

    .slots-page__modal-primary {
      border: none;
      background: #2563eb;
      color: #ffffff;
    }

    @media (max-width: 900px) {
      .slots-page__toolbar {
        align-items: stretch;
        flex-direction: column;
      }

      .slots-page__day-nav {
        width: 100%;
      }

      .slots-page__staff-summary {
        grid-template-columns: repeat(2, minmax(0, 1fr));
      }

      .slots-page__modal-grid {
        grid-template-columns: minmax(0, 1fr);
      }
    }

    @media (max-width: 640px) {
      .slots-page__view-switch {
        width: 100%;
      }

      .slots-page__view-button {
        flex: 1 1 0;
        min-width: 0;
      }

      .slots-page__staff-summary {
        grid-template-columns: minmax(0, 1fr);
      }

      .slots-page__modal-actions {
        flex-direction: column;
      }

      .slots-page__modal-secondary,
      .slots-page__modal-primary {
        width: 100%;
      }
    }
  `]
})
export class SlotsPageComponent implements OnInit, OnDestroy
{
  private static readonly PAGE_SIZE = 100;
  private static readonly CALENDAR_PAGE_SIZE = 200;

  private readonly slotsService = inject(SlotsService);
  private readonly devicesService = inject(DevicesService);
  private readonly usersService = inject(UsersService);
  private readonly settingsService = inject(SettingsService);
  private readonly authService = inject(AuthService);
  private readonly systemAdminContext = inject(SystemAdminContextService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly formBuilder = inject(FormBuilder);
  private readonly subscriptions = new Subscription();

  protected items: SlotItem[] = [];
  protected clinicOptions: Array<{ id: string; label: string }> = [];
  protected deviceOptions: Array<{ id: string; label: string; clinicId?: string | null }> = [];
  protected specialistOptions: Array<{ id: string; label: string; clinicId?: string | null }> = [];
  protected clinicTimeZones: Record<string, string> = {};
  protected companyTimeZone = 'UTC';
  protected isLoading = true;
  protected isLoadingMore = false;
  protected hasMoreSlots = false;
  protected nextCursor: string | null = null;
  protected errorText: string | null = null;
  private appliedFilters: SlotListFilters = {};
  protected selectedDayKey = this.getTodayDateKey('UTC');
  private appliedSort: SlotListSort = 'startAtAsc';
  private slotsLoadSequence = 0;
  protected viewMode: SlotsViewMode = 'table';
  protected canCreateAppointment = false;
  protected canOpenDevice = false;
  protected canOpenClinic = false;
  protected canOpenSpecialist = false;
  protected isFilterModalOpen = false;
  protected isCreateAppointmentModalOpen = false;
  protected isDeviceQuickViewOpen = false;
  protected isClinicQuickViewOpen = false;
  protected isSpecialistQuickViewOpen = false;
  protected appointmentCreatePrefill: AppointmentFormPrefill = {};
  protected isSystemAdmin = false;
  protected selectedDeviceId: string | null = null;
  protected selectedClinicId: string | null = null;
  protected selectedSpecialistUserId: string | null = null;
  protected selectedGlobalClinicId: string | null = null;
  protected readonly filtersForm = this.formBuilder.group({
    deviceId: [''],
    specialistUserId: [''],
    status: ['all'],
    sort: ['startAtAsc'],
  });
  protected readonly slotStatuses: Array<NonNullable<SlotListFilters['status']>> = [
    'available',
    'reserved',
    'blocked',
    'completed',
  ];
  protected readonly slotSorts: SlotListSort[] = [
    'startAtAsc',
    'startAtDesc',
    'createdAtDesc',
    'createdAtAsc',
  ];
  protected readonly calendarSelectableStatuses = ['available'];
  private filterOptionsLoaded = false;
  private filterOptionsLoading = false;

  public ngOnInit(): void
  {
    const role = this.authService.getCurrentRole();
    this.isSystemAdmin = role === 'system_admin';
    this.canCreateAppointment =
      role === 'system_admin'
      || role === 'company_admin'
      || role === 'manager'
      || role === 'reception';
    this.canOpenDevice =
      role === 'system_admin'
      || role === 'company_admin'
      || role === 'manager'
      || role === 'reception';
    this.canOpenClinic = this.canOpenDevice;
    this.canOpenSpecialist = this.canOpenDevice;
    this.viewMode = this.normalizeViewMode(this.route.snapshot.queryParamMap.get('view'));
    this.selectedDayKey = this.resolveInitialSelectedDayKey();
    this.selectedGlobalClinicId = this.systemAdminContext.getClinicId();
    if (this.selectedGlobalClinicId) {
      this.appliedFilters = {
        ...this.appliedFilters,
        clinicId: this.selectedGlobalClinicId.trim(),
      };
    }
    this.applyDayFilters(this.selectedDayKey);
    this.loadSlots({ append: false });

    this.subscriptions.add(this.systemAdminContext.clinicId$.pipe(
      distinctUntilChanged(),
      skip(1),
    ).subscribe((clinicId) => {
      this.selectedGlobalClinicId = clinicId;
      this.appliedFilters = {
        ...this.appliedFilters,
        clinicId: clinicId?.trim() || undefined,
      };
      this.resetFiltersForm();
      this.applyDayFilters(this.selectedDayKey, this.appliedFilters);
      this.loadSlots({ append: false });
    }));

    if (this.isSystemAdmin) {
      this.subscriptions.add(this.systemAdminContext.companyId$.pipe(
        distinctUntilChanged(),
        skip(1),
      ).subscribe(() => {
        this.selectedGlobalClinicId = this.systemAdminContext.getClinicId();
        this.appliedFilters = {
          clinicId: this.selectedGlobalClinicId?.trim() || undefined,
        };
        this.appliedSort = 'startAtAsc';
        this.resetFiltersForm();
        this.applyDayFilters(
          this.normalizeSelectedDayKey(),
          this.appliedFilters,
        );
        this.resetFilterOptions();
        this.loadSlots({ append: false });
      }));
    }
  }

  public ngOnDestroy(): void
  {
    this.subscriptions.unsubscribe();
  }

  protected setViewMode(mode: SlotsViewMode): void
  {
    if (this.viewMode === mode) {
      return;
    }

    const wasFullDayView = this.isFullDayViewMode();
    this.viewMode = mode;
    this.persistViewMode(mode);
    if (this.isFullDayViewMode(mode) && !wasFullDayView) {
      this.loadSlots({ append: false });
    }
  }

  private persistViewMode(mode: SlotsViewMode): void
  {
    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        view: mode === 'table' ? null : mode,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private persistSelectedDay(dayKey: string): void
  {
    if (this.route.snapshot.queryParamMap.get('date') === dayKey)
    {
      return;
    }

    void this.router.navigate([], {
      relativeTo: this.route,
      queryParams: {
        date: dayKey,
      },
      queryParamsHandling: 'merge',
      replaceUrl: true,
    });
  }

  private normalizeViewMode(value: string | null | undefined): SlotsViewMode
  {
    switch (value) {
      case 'calendar':
      case 'capacity':
        return value;
      case 'table':
      default:
        return 'table';
    }
  }

  protected get calendarRangeStartDate(): string | null
  {
    const from = this.appliedFilters.dateFrom;
    const to = this.appliedFilters.dateTo;
    if (!from || !to)
    {
      return null;
    }

    const effectiveTimeZone = this.getEffectiveSchedulingTimeZone();
    const fromKey = this.toDateKeyInTimeZone(from, effectiveTimeZone);
    const toKey = this.toDateKeyInTimeZone(to, effectiveTimeZone);
    if (!fromKey || !toKey || fromKey === toKey)
    {
      return null;
    }

    return fromKey;
  }

  protected get calendarRangeEndDate(): string | null
  {
    const from = this.appliedFilters.dateFrom;
    const to = this.appliedFilters.dateTo;
    if (!from || !to)
    {
      return null;
    }

    const effectiveTimeZone = this.getEffectiveSchedulingTimeZone();
    const fromKey = this.toDateKeyInTimeZone(from, effectiveTimeZone);
    const toKey = this.toDateKeyInTimeZone(to, effectiveTimeZone);
    if (!fromKey || !toKey || fromKey === toKey)
    {
      return null;
    }

    return toKey;
  }

  protected get staffCalendarSummaryItems(): SlotsStaffSummaryItem[]
  {
    const summary = this.buildLoadedSlotSummary();

    return [
      {
        label: 'slots.staffSummary.available',
        value: String(summary.available),
        tone: summary.available > 0 ? 'good' : 'danger',
      },
      {
        label: 'slots.staffSummary.reserved',
        value: String(summary.reserved),
        tone: summary.reserved > 0 ? 'warning' : 'neutral',
      },
      {
        label: 'slots.staffSummary.blocked',
        value: String(summary.blocked),
        tone: summary.blocked > 0 ? 'danger' : 'neutral',
      },
      {
        label: 'slots.staffSummary.completed',
        value: String(summary.completed),
        tone: 'neutral',
      },
      {
        label: 'slots.staffSummary.specialists',
        value: `${summary.availableSpecialistSlots}/${summary.totalSpecialistSlots}`,
        tone: summary.totalSpecialistSlots > 0 && summary.availableSpecialistSlots === 0 ? 'danger' : 'neutral',
      },
      {
        label: 'slots.staffSummary.devices',
        value: `${summary.availableDeviceSlots}/${summary.totalDeviceSlots}`,
        tone: summary.totalDeviceSlots > 0 && summary.availableDeviceSlots === 0 ? 'danger' : 'neutral',
      },
    ];
  }

  protected get staffCapacitySummaryItems(): SlotsStaffSummaryItem[]
  {
    const summary = this.buildLoadedSlotSummary(this.capacityVisibleItems);

    return [
      {
        label: 'slots.staffSummary.capacityWindows',
        value: String(summary.capacityWindows),
        tone: summary.capacityWindows > 0 ? 'good' : 'danger',
      },
      {
        label: 'slots.staffSummary.hardUnavailable',
        value: String(summary.hardUnavailableWindows),
        tone: summary.hardUnavailableWindows > 0 ? 'danger' : 'neutral',
      },
      {
        label: 'slots.staffSummary.specialistCapacity',
        value: `${summary.availableSpecialistSlots}/${summary.totalSpecialistSlots}`,
        tone: summary.totalSpecialistSlots > 0 && summary.availableSpecialistSlots === 0 ? 'danger' : 'neutral',
      },
      {
        label: 'slots.staffSummary.deviceCapacity',
        value: `${summary.availableDeviceSlots}/${summary.totalDeviceSlots}`,
        tone: summary.totalDeviceSlots > 0 && summary.availableDeviceSlots === 0 ? 'danger' : 'neutral',
      },
    ];
  }

  protected get calendarItems(): SlotCalendarItem[]
  {
    return this.items.map((item) => ({
      id: item.id,
      startTime: item.startTime,
      endTime: item.endTime,
      status: item.status,
      timeZone: item.clinicTimeZone?.trim()
        || this.clinicTimeZones[item.clinicId]?.trim()
        || this.getEffectiveSchedulingTimeZone(item.clinicId),
      primaryLabel: this.buildCalendarPrimaryLabel(item),
      secondaryLabel: this.buildCalendarSecondaryLabel(item),
      metaLabel: this.buildCalendarMetaLabel(item),
    }));
  }

  protected get capacityCalendarItems(): SlotCalendarItem[]
  {
    const items = this.capacityVisibleItems;
    const groups = new Map<string, {
      representative: SlotItem;
      specialistTotal: number;
      specialistAvailable: number;
      deviceTotal: number;
      deviceAvailable: number;
      blocked: number;
      reserved: number;
      completed: number;
      bookableStartSlotId: string | null;
    }>();

    for (const item of items) {
      const key = `${item.startTime}|${item.endTime}`;
      const group = groups.get(key) ?? {
        representative: item,
        specialistTotal: 0,
        specialistAvailable: 0,
        deviceTotal: 0,
        deviceAvailable: 0,
        blocked: 0,
        reserved: 0,
        completed: 0,
        bookableStartSlotId: null,
      };

      const effectiveStatus = item.status;

      if (item.specialistUserId) {
        group.specialistTotal += 1;
        if (effectiveStatus === 'available') {
          group.specialistAvailable += 1;
          group.bookableStartSlotId ??= item.id;
        }
      }

      if (item.deviceId) {
        group.deviceTotal += 1;
        if (effectiveStatus === 'available') {
          group.deviceAvailable += 1;
        }
      }

      if (effectiveStatus === 'blocked') {
        group.blocked += 1;
      } else if (effectiveStatus === 'reserved') {
        group.reserved += 1;
      } else if (effectiveStatus === 'completed') {
        group.completed += 1;
      }

      groups.set(key, group);
    }

    return Array.from(groups.entries())
      .sort(([left], [right]) => left.localeCompare(right))
      .map(([key, group]) => {
        const totalCount = group.specialistTotal + group.deviceTotal;
        const availableCount = group.specialistAvailable + group.deviceAvailable;
        const occupiedCount = Math.max(0, totalCount - availableCount);
        const metaParts = [
          group.blocked > 0 ? `B ${group.blocked}` : '',
          group.reserved > 0 ? `R ${group.reserved}` : '',
          group.completed > 0 ? `C ${group.completed}` : '',
        ].filter(Boolean);

        return {
          id: `capacity|${key}`,
          startTime: group.representative.startTime,
          endTime: group.representative.endTime,
          status: group.bookableStartSlotId ? 'available' : 'blocked',
          timeZone: group.representative.clinicTimeZone?.trim()
            || this.clinicTimeZones[group.representative.clinicId]?.trim()
            || this.getEffectiveSchedulingTimeZone(group.representative.clinicId),
          primaryLabel: `S ${group.specialistAvailable}/${group.specialistTotal} / D ${group.deviceAvailable}/${group.deviceTotal}`,
          secondaryLabel: group.bookableStartSlotId ? 'Capacity available' : 'No bookable specialist',
          metaLabel: metaParts.length > 0 ? metaParts.join(' / ') : null,
          availableCount,
          occupiedCount,
          totalCount,
          hardUnavailable: totalCount > 0 && availableCount === 0,
        };
      });
  }

  protected get selectedSchedulingTimeZone(): string
  {
    return this.getEffectiveSchedulingTimeZone();
  }

  private get capacityVisibleItems(): SlotItem[]
  {
    if (this.isSystemAdmin) {
      return this.items;
    }

    return this.items.filter((item) => !item.workingWindowArtifact);
  }

  private buildLoadedSlotSummary(items: SlotItem[] = this.items): {
    available: number;
    reserved: number;
    blocked: number;
    completed: number;
    totalSpecialistSlots: number;
    availableSpecialistSlots: number;
    totalDeviceSlots: number;
    availableDeviceSlots: number;
    capacityWindows: number;
    hardUnavailableWindows: number;
  } {
    const byWindow = new Map<string, {
      total: number;
      available: number;
    }>();
    const summary = {
      available: 0,
      reserved: 0,
      blocked: 0,
      completed: 0,
      totalSpecialistSlots: 0,
      availableSpecialistSlots: 0,
      totalDeviceSlots: 0,
      availableDeviceSlots: 0,
      capacityWindows: 0,
      hardUnavailableWindows: 0,
    };

    for (const item of items) {
      const effectiveStatus = item.status;

      if (effectiveStatus === 'available') {
        summary.available += 1;
      } else if (effectiveStatus === 'reserved') {
        summary.reserved += 1;
      } else if (effectiveStatus === 'blocked') {
        summary.blocked += 1;
      } else if (effectiveStatus === 'completed') {
        summary.completed += 1;
      }

      if (item.specialistUserId) {
        summary.totalSpecialistSlots += 1;
        if (effectiveStatus === 'available') {
          summary.availableSpecialistSlots += 1;
        }
      }

      if (item.deviceId) {
        summary.totalDeviceSlots += 1;
        if (effectiveStatus === 'available') {
          summary.availableDeviceSlots += 1;
        }
      }

      const windowKey = `${item.startTime}|${item.endTime}`;
      const windowSummary = byWindow.get(windowKey) ?? { total: 0, available: 0 };
      windowSummary.total += 1;
      if (effectiveStatus === 'available') {
        windowSummary.available += 1;
      }
      byWindow.set(windowKey, windowSummary);
    }

    for (const windowSummary of byWindow.values()) {
      summary.capacityWindows += 1;
      if (windowSummary.total > 0 && windowSummary.available === 0) {
        summary.hardUnavailableWindows += 1;
      }
    }

    return summary;
  }

  protected onSelectedDayChanged(dateKey: string): void
  {
    const target = dateKey?.trim() || this.getTodayDateKey(this.getEffectiveSchedulingTimeZone());
    this.applyDayFilters(target);
    this.loadSlots({ append: false });
  }

  protected shiftSelectedDay(daysOffset: number): void
  {
    this.applyDayFilters(this.addDaysToDateKey(this.selectedDayKey, daysOffset));
    this.loadSlots({ append: false });
  }

  protected goToToday(): void
  {
    this.applyDayFilters(this.getTodayDateKey(this.getEffectiveSchedulingTimeZone()));
    this.loadSlots({ append: false });
  }

  protected onCalendarSlotSelected(slotId: string): void
  {
    if (!this.canCreateAppointment)
    {
      return;
    }

    const isCapacityWindow = slotId.startsWith('capacity|');
    const slot = this.items.find((item) => item.id === slotId)
      ?? this.resolveCapacityWindowCreateSlot(slotId);
    if (!slot)
    {
      return;
    }

    this.onCreateAppointment(slot, { preserveTimeOnly: isCapacityWindow });
  }

  private resolveCapacityWindowCreateSlot(slotId: string): SlotItem | null
  {
    const prefix = 'capacity|';
    if (!slotId.startsWith(prefix)) {
      return null;
    }

    const [startTime, endTime] = slotId.slice(prefix.length).split('|');
    if (!startTime || !endTime) {
      return null;
    }

    return this.items
      .filter((item) =>
        item.startTime === startTime &&
        item.endTime === endTime &&
        item.status === 'available' &&
        !!item.specialistUserId &&
        !item.deviceId,
      )
      .sort((left, right) => {
        const leftLabel = left.specialistLabel?.trim() ?? left.specialistUserId ?? '';
        const rightLabel = right.specialistLabel?.trim() ?? right.specialistUserId ?? '';
        return leftLabel.localeCompare(rightLabel);
      })[0] ?? null;
  }

  protected get visibleDeviceOptions(): Array<{ id: string; label: string; clinicId?: string | null }>
  {
    const clinicId = this.selectedGlobalClinicId?.trim() ?? this.appliedFilters.clinicId?.trim() ?? '';
    if (!clinicId) {
      return this.deviceOptions;
    }

    return this.deviceOptions.filter((item) => !item.clinicId || item.clinicId === clinicId);
  }

  protected get visibleSpecialistOptions(): Array<{ id: string; label: string; clinicId?: string | null }>
  {
    const clinicId = this.selectedGlobalClinicId?.trim() ?? this.appliedFilters.clinicId?.trim() ?? '';
    if (!clinicId) {
      return this.specialistOptions;
    }

    return this.specialistOptions.filter((item) => !item.clinicId || item.clinicId === clinicId);
  }

  protected openFilterModal(): void
  {
    this.loadFilterOptions();
    this.filtersForm.reset({
      deviceId: this.appliedFilters.deviceId?.trim() ?? '',
      specialistUserId: this.appliedFilters.specialistUserId?.trim() ?? '',
      status: this.appliedFilters.status?.trim() ?? 'all',
      sort: this.appliedSort,
    });
    this.isFilterModalOpen = true;
  }

  protected closeFilterModal(): void
  {
    this.isFilterModalOpen = false;
  }

  protected onApplyFilters(): void
  {
    const value = this.filtersForm.getRawValue();
    this.appliedSort = this.normalizeSort(value.sort);
    this.applyDayFilters(this.selectedDayKey, {
      ...this.appliedFilters,
      clinicId: this.isSystemAdmin
        ? this.selectedGlobalClinicId?.trim() || undefined
        : this.appliedFilters.clinicId,
      deviceId: value.deviceId?.trim() || undefined,
      specialistUserId: value.specialistUserId?.trim() || undefined,
      status: this.normalizeStatus(value.status),
    });
    this.loadSlots({ append: false });
  }

  protected onResetFilters(): void
  {
    this.resetFiltersForm();
    this.appliedSort = 'startAtAsc';
    this.applyDayFilters(this.selectedDayKey, {
      clinicId: this.isSystemAdmin
        ? this.selectedGlobalClinicId?.trim() || undefined
        : this.appliedFilters.clinicId,
    });
    this.loadSlots({ append: false });
  }

  protected onCreateAppointment(
    slot: SlotItem,
    options: { preserveTimeOnly?: boolean } = {},
  ): void
  {
    if (!this.canCreateFromSlot(slot))
    {
      return;
    }

    this.appointmentCreatePrefill = {
      clinicId: slot.clinicId,
      deviceId: options.preserveTimeOnly ? '' : (slot.deviceId ?? ''),
      firstSlotId: options.preserveTimeOnly ? '' : slot.id,
      firstSlotStartTime: slot.startTime,
      firstSlotEndTime: slot.endTime,
      preserveTimeOnly: !!options.preserveTimeOnly,
      specialistUserId: options.preserveTimeOnly ? '' : (slot.specialistUserId ?? '')
    };
    this.isCreateAppointmentModalOpen = true;
  }

  protected closeCreateAppointmentModal(): void
  {
    this.isCreateAppointmentModalOpen = false;
  }

  protected openDeviceQuickView(deviceId: string): void
  {
    const normalized = deviceId.trim();
    if (!this.canOpenDevice || !normalized) {
      return;
    }

    this.selectedDeviceId = normalized;
    this.isDeviceQuickViewOpen = true;
  }

  protected closeDeviceQuickView(): void
  {
    this.isDeviceQuickViewOpen = false;
    this.selectedDeviceId = null;
  }

  protected openClinicQuickView(clinicId: string): void
  {
    const normalized = clinicId.trim();
    if (!this.canOpenClinic || !normalized) {
      return;
    }

    this.selectedClinicId = normalized;
    this.isClinicQuickViewOpen = true;
  }

  protected closeClinicQuickView(): void
  {
    this.isClinicQuickViewOpen = false;
    this.selectedClinicId = null;
  }

  protected openSpecialistQuickView(specialistUserId: string): void
  {
    const normalized = specialistUserId.trim();
    if (!this.canOpenSpecialist || !normalized) {
      return;
    }

    this.selectedSpecialistUserId = normalized;
    this.isSpecialistQuickViewOpen = true;
  }

  protected closeSpecialistQuickView(): void
  {
    this.isSpecialistQuickViewOpen = false;
    this.selectedSpecialistUserId = null;
  }

  protected handleAppointmentCreatedFromModal(): void
  {
    this.isCreateAppointmentModalOpen = false;
    this.loadSlots({ append: false });
  }

  protected loadMoreSlots(): void
  {
    if (this.isLoading || this.isLoadingMore || !this.hasMoreSlots || this.isFullDayViewMode()) {
      return;
    }

    this.loadSlots({ append: true });
  }

  private loadSlots(options: { append: boolean }): void
  {
    if (!this.hasRequiredCompanyContext()) {
      this.items = [];
      this.errorText = 'error.settingsCompanyContextRequired';
      this.isLoading = false;
      this.isLoadingMore = false;
      this.hasMoreSlots = false;
      this.nextCursor = null;
      return;
    }

    if (options.append) {
      this.isLoadingMore = true;
    } else {
      this.isLoading = true;
      this.items = [];
      this.nextCursor = null;
      this.hasMoreSlots = false;
    }
    this.errorText = null;

    this.loadSlotsPage({
      append: options.append,
      cursor: options.append ? this.nextCursor : null,
      loadFullDay: this.isFullDayViewMode(),
      loadId: ++this.slotsLoadSequence,
    });
  }

  private isFullDayViewMode(mode: SlotsViewMode = this.viewMode): boolean
  {
    return mode !== 'table';
  }

  private loadSlotsPage(options: {
    append: boolean;
    cursor: string | null;
    loadFullDay: boolean;
    loadId: number;
  }): void
  {
    this.slotsService.getPage({
      limit: options.loadFullDay
        ? SlotsPageComponent.CALENDAR_PAGE_SIZE
        : SlotsPageComponent.PAGE_SIZE,
      cursor: options.cursor,
      filters: this.appliedFilters,
      sort: this.appliedSort,
    }).subscribe({
      next: (page: SlotListPage) =>
      {
        if (options.loadId !== this.slotsLoadSequence) {
          return;
        }

        this.items = options.append
          ? [...this.items, ...page.items]
          : page.items;
        this.nextCursor = page.nextCursor;
        this.hasMoreSlots = page.hasMore;

        if (options.loadFullDay && page.hasMore && page.nextCursor) {
          this.loadSlotsPage({
            append: true,
            cursor: page.nextCursor,
            loadFullDay: true,
            loadId: options.loadId,
          });
          return;
        }

        this.isLoading = false;
        this.isLoadingMore = false;
      },
      error: (error: unknown) =>
      {
        if (options.loadId !== this.slotsLoadSequence) {
          return;
        }

        console.error('Failed to load slots', error);
        this.errorText = resolveHttpErrorMessageKey(error, {
          forbidden: 'error.forbiddenSlotsList',
          fallback: 'Failed to load slots'
        });
        this.isLoading = false;
        this.isLoadingMore = false;
      }
    });
  }

  protected canCreateFromSlot(slot: SlotItem): boolean
  {
    if (slot.status !== 'available')
    {
      return false;
    }

    return new Date(slot.startTime).getTime() > Date.now();
  }

  private buildCalendarPrimaryLabel(item: SlotItem): string
  {
    const clinic = item.clinicLabel?.trim()
      || this.clinicOptions.find((entry) => entry.id === item.clinicId)?.label?.trim()
      || '';
    const device = item.deviceLabel?.trim()
      || this.deviceOptions.find((entry) => entry.id === item.deviceId)?.label?.trim()
      || '';

    if (clinic && device) {
      return `${clinic} • ${device}`;
    }

    if (device) {
      return device;
    }

    if (clinic) {
      return clinic;
    }

    return item.source === 'intersection' ? 'Device + specialist' : 'Slot';
  }

  private buildCalendarSecondaryLabel(item: SlotItem): string | null
  {
    const specialist = item.specialistLabel?.trim()
      || this.specialistOptions.find((entry) => entry.id === item.specialistUserId)?.label?.trim()
      || '';
    if (specialist) {
      return specialist;
    }

    if (item.specialistUserId) {
      return 'Specialist';
    }

    return null;
  }

  private buildCalendarMetaLabel(item: SlotItem): string | null
  {
    const reason = item.availabilityReason?.trim() ?? '';
    if (reason) {
      return reason;
    }

    if (item.source === 'intersection') {
      return 'Intersection view';
    }

    return item.source?.trim() || null;
  }

  private loadFilterOptions(): void
  {
    if (this.filterOptionsLoaded || this.filterOptionsLoading) {
      return;
    }

    if (!this.hasRequiredCompanyContext()) {
      this.clinicOptions = [];
      this.clinicTimeZones = {};
      this.companyTimeZone = 'UTC';
      this.deviceOptions = [];
      this.specialistOptions = [];
      return;
    }

    this.filterOptionsLoading = true;

    this.settingsService.getClinicOperationalSettings().subscribe({
      next: (items: ClinicOperationalSettingsDto[]) =>
      {
        this.clinicTimeZones = items.reduce<Record<string, string>>((acc, item) => {
          const timeZone = item.timeZone?.trim() ?? '';
          if (timeZone) {
            acc[item.clinicId] = timeZone;
          }
          return acc;
        }, {});
        this.clinicOptions = items.map((item) => ({
          id: item.clinicId,
          label: item.clinicName,
        }));
        this.companyTimeZone = items[0]?.timeZoneInherited?.trim() || this.companyTimeZone;
        this.filterOptionsLoaded = true;
        this.filterOptionsLoading = false;
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load clinic options for slots table', error);
        this.clinicTimeZones = {};
        this.clinicOptions = [];
        this.filterOptionsLoading = false;
      },
    });

    this.devicesService.getList().subscribe({
      next: (items: DeviceItem[]) =>
      {
        this.deviceOptions = items
          .filter((item) => item.status === 'active')
          .map((item) => ({
            id: item.id,
            label: this.formatDeviceLabel(item),
            clinicId: item.clinicId,
          }));
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load device options for slots filters', error);
        this.deviceOptions = [];
      }
    });

    this.usersService.getList().subscribe({
      next: (items: UserItem[]) =>
      {
        this.specialistOptions = items
          .filter((item) => item.role === 'specialist' && item.isActive)
          .map((item) => ({
            id: item.id,
            label: item.email,
            clinicId: item.clinicId,
          }));
      },
      error: (error: unknown) =>
      {
        console.error('Failed to load specialist options for slots filters', error);
        this.specialistOptions = [];
      }
    });
  }

  private resetFilterOptions(): void
  {
    this.filterOptionsLoaded = false;
    this.filterOptionsLoading = false;
    this.clinicOptions = [];
    this.clinicTimeZones = {};
    this.deviceOptions = [];
    this.specialistOptions = [];
  }

  private applyDayFilters(dayKey: string, baseFilters?: SlotListFilters): void
  {
    this.selectedDayKey = dayKey;
    this.persistSelectedDay(dayKey);
    this.appliedFilters = {
      ...(baseFilters ?? this.appliedFilters),
      dateFrom: undefined,
      dateTo: undefined,
      schedulingDate: dayKey,
    };
  }

  private resolveInitialSelectedDayKey(): string
  {
    const queryDate = this.route.snapshot.queryParamMap.get('date')?.trim() ?? '';
    return this.parseDateKey(queryDate)
      ? queryDate
      : this.selectedDayKey;
  }

  private normalizeSelectedDayKey(): string
  {
    return this.parseDateKey(this.selectedDayKey)
      ? this.selectedDayKey
      : this.getTodayDateKey(this.getEffectiveSchedulingTimeZone());
  }

  private resetFiltersForm(): void
  {
    this.filtersForm.reset({
      deviceId: '',
      specialistUserId: '',
      status: 'all',
      sort: 'startAtAsc',
    });
  }

  private normalizeStatus(value: string | null | undefined): SlotListFilters['status'] | undefined
  {
    switch (value) {
      case 'available':
      case 'reserved':
      case 'blocked':
      case 'completed':
        return value;
      default:
        return undefined;
    }
  }

  private normalizeSort(value: string | null | undefined): SlotListSort
  {
    switch (value) {
      case 'startAtDesc':
      case 'createdAtDesc':
      case 'createdAtAsc':
        return value;
      case 'startAtAsc':
      default:
        return 'startAtAsc';
    }
  }

  private formatDateKey(value: Date): string
  {
    const year = value.getFullYear();
    const month = String(value.getMonth() + 1).padStart(2, '0');
    const day = String(value.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private parseDateKey(value: string): Date | null
  {
    const normalized = value?.trim() ?? '';
    if (!normalized) {
      return null;
    }

    const match = normalized.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) {
      return null;
    }

    const [, year, month, day] = match;
    const parsed = new Date(Number(year), Number(month) - 1, Number(day));
    if (Number.isNaN(parsed.getTime())) {
      return null;
    }

    parsed.setHours(0, 0, 0, 0);
    return parsed;
  }

  private toDateKeyInTimeZone(value: string, timeZone: string): string | null
  {
    const parsed = new Date(value);
    if (Number.isNaN(parsed.getTime()))
    {
      return null;
    }

    const parts = new Intl.DateTimeFormat('en-CA', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hourCycle: 'h23',
      timeZone,
    }).formatToParts(parsed);

    const values = Object.fromEntries(
      parts
        .filter((part) => part.type !== 'literal')
        .map((part) => [part.type, part.value]),
    );

    return `${values['year']}-${values['month']}-${values['day']}`;
  }

  private getEffectiveSchedulingTimeZone(clinicId?: string): string
  {
    const effectiveClinicId = clinicId?.trim()
      || this.appliedFilters.clinicId?.trim()
      || this.selectedGlobalClinicId?.trim()
      || '';

    return this.clinicTimeZones[effectiveClinicId]?.trim()
      || this.companyTimeZone?.trim()
      || 'UTC';
  }

  private addDaysToDateKey(dateKey: string, offsetDays: number): string
  {
    const parsed = this.parseDateKey(dateKey);
    if (!parsed) {
      return dateKey;
    }

    const next = new Date(parsed);
    next.setDate(next.getDate() + offsetDays);
    return this.formatDateKey(next);
  }

  private getTodayDateKey(timeZone: string): string
  {
    return this.toDateKeyInTimeZone(new Date().toISOString(), timeZone) ?? this.formatDateKey(new Date());
  }

  private formatDeviceLabel(item: DeviceItem): string
  {
    const parts = [item.brand, item.model, item.serialNumber]
      .map((part) => part?.trim())
      .filter((part): part is string => Boolean(part));

    if (parts.length === 0)
    {
      return item.id;
    }

    return parts.join(' • ');
  }

  private hasRequiredCompanyContext(): boolean {
    if (!this.isSystemAdmin) {
      return true;
    }

    return !!this.systemAdminContext.getCompanyId()?.trim();
  }

  protected get companyContextMissing(): boolean {
    return !this.hasRequiredCompanyContext();
  }
}
